import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { PeriodProvider } from "@/contexts/PeriodContext";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PublisherSelection from "@/pages/PublisherSelection";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Reports from "@/pages/Reports";
import RollForward from "@/pages/RollForward";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import Benutzer from "@/pages/Benutzer";
import ImportPage from "@/pages/Import";
import ExportPage from "@/pages/Export";
import HkPflege from "@/pages/HkPflege";
import VorschauPflege from "@/pages/VorschauPflege";
import Snapshots from "@/pages/Snapshots";
import SonderWb from "@/pages/SonderWb";

// Diese Pfade sind mandanten-übergreifend und dürfen auch OHNE aktiven
// Verlag aufgerufen werden (z. B. direkt aus der Verlagsauswahl).
const CROSS_TENANT_PATHS = new Set(["/benutzer"]);

function ProtectedRoute({ children, requireTenant = true }) {
    const { user, loading } = useAuth();
    const { activeTenant, loaded } = useTenant();
    const location = useLocation();
    if (loading || !loaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
                <span className="text-sm tracking-wider uppercase">Lade…</span>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    const needsTenant = requireTenant && !CROSS_TENANT_PATHS.has(location.pathname);
    if (needsTenant && !activeTenant) return <Navigate to="/verlag-waehlen" replace />;
    return children;
}

function AppRouter() {
    const location = useLocation();
    // CRITICAL: Detect session_id synchronously to avoid race with /auth/me
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route
                path="/verlag-waehlen"
                element={
                    <ProtectedRoute requireTenant={false}>
                        <PublisherSelection />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <AppShell />
                    </ProtectedRoute>
                }
            >
                <Route index element={<Dashboard />} />
                <Route path="uebersicht" element={<Dashboard />} />
                <Route path="inventar" element={<Inventory />} />
                <Route path="berichte" element={<Reports />} />
                <Route path="rollforward" element={<RollForward />} />
                <Route path="import" element={<ImportPage />} />
                <Route path="hk-pflege" element={<HkPflege />} />
                <Route path="vorschau-pflege" element={<VorschauPflege />} />
                <Route path="snapshots" element={<Snapshots />} />
                <Route path="sonder-wb" element={<SonderWb />} />
                <Route path="export" element={<ExportPage />} />
                <Route path="benutzer" element={<Benutzer />} />
                <Route path="einstellungen" element={<Settings />} />
                <Route path="hilfe" element={<Help />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
            <div className="App">
                <BrowserRouter>
                    <AuthProvider>
                        <TenantProvider>
                            <PeriodProvider>
                                <AppRouter />
                                <Toaster richColors position="top-right" />
                            </PeriodProvider>
                        </TenantProvider>
                    </AuthProvider>
                </BrowserRouter>
            </div>
        </ThemeProvider>
    );
}
